/* BAC Calculator — Australia Breathalyser.
   Math core + <bac-calculator> UI custom element.
   Estimates only — never a substitute for a certified breath test.
   Metric Widmark: BAC% = grams / (weightKg * r * 10) - 0.015 * hours
   1 AU standard drink = 10 g ethanol; ethanol density 0.789 g/mL. */
(function (root) {
  'use strict';

  var BACCalc = {
    ETHANOL_DENSITY: 0.789,
    STANDARD_DRINK_GRAMS: 10,
    ELIMINATION_RATE: 0.015,
    R: { male: 0.68, female: 0.55 },
    /* professional/heavy-vehicle is zero in most states (0.02 only in NSW/ACT);
       we use the stricter zero so the calculator never overstates a legal margin */
    LIMITS: { full: 0.05, learner: 0, p1: 0, p2: 0, professional: 0 },
    gramsOfAlcohol: function (volumeMl, abvPercent) {
      return volumeMl * (abvPercent / 100) * BACCalc.ETHANOL_DENSITY;
    },
    standardDrinks: function (grams) {
      return grams / BACCalc.STANDARD_DRINK_GRAMS;
    },
    peak: function (o) {
      return o.grams / (o.weightKg * o.r * 10);
    },
    bacAt: function (o) {
      return Math.max(0, BACCalc.peak(o) - BACCalc.ELIMINATION_RATE * o.hours);
    },
    hoursUntil: function (target, o) {
      var h = (BACCalc.peak(o) - target) / BACCalc.ELIMINATION_RATE;
      return Math.max(0, h);
    },
    curve: function (o) {
      var maxHours = o.maxHours || 24;
      var pts = [];
      var h = 0;
      var bac;
      do {
        bac = BACCalc.bacAt({ grams: o.grams, weightKg: o.weightKg, r: o.r, hours: h });
        pts.push({ h: h, bac: bac });
        h += 0.25;
      } while (bac > 0 && h <= maxHours);
      return pts;
    }
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = BACCalc;
  root.BACCalc = BACCalc;

  /* ---------------- UI custom element (browser only) ---------------- */
  if (typeof HTMLElement === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('bac-calculator')) return;

  var EFFECTS = [
    { min: 0.30, text: 'Extremely dangerous range. Risk of unconsciousness and alcohol poisoning. Seek help if someone reaches this level.' },
    { min: 0.15, text: 'Serious impairment: major loss of coordination, blurred vision, badly degraded judgement and reaction time.' },
    { min: 0.08, text: 'Well over the Australian full-licence limit. Reaction time, vision and judgement are all significantly impaired.' },
    { min: 0.05, text: 'At or above the 0.05 full-licence limit. Inhibitions and judgement are measurably affected. Do not drive.' },
    { min: 0.02, text: 'Mild relaxation and warmth. Already over the limit for learner, P-plate and many professional drivers.' },
    { min: 0.0001, text: 'Some alcohol present. Any reading above zero is over the limit for learner and provisional licence holders.' },
    { min: 0, text: 'No measurable alcohol on these inputs. Remember this is an estimate, not a reading.' }
  ];

  function fmtHours(h) {
    if (!isFinite(h) || h <= 0) return '0 min';
    var whole = Math.floor(h);
    var mins = Math.round((h - whole) * 60);
    if (mins === 60) { whole += 1; mins = 0; }
    if (whole === 0) return mins + ' min';
    return whole + ' h ' + (mins < 10 ? '0' : '') + mins + ' min';
  }

  function BACCalculatorEl() {
    return Reflect.construct(HTMLElement, [], BACCalculatorEl);
  }
  BACCalculatorEl.prototype = Object.create(HTMLElement.prototype);
  BACCalculatorEl.prototype.constructor = BACCalculatorEl;

  BACCalculatorEl.prototype.connectedCallback = function () {
    var presetsEl = this.querySelector('[data-bac-presets]');
    try {
      this.presets = presetsEl ? JSON.parse(presetsEl.textContent) : [];
    } catch (e) {
      this.presets = [];
    }
    this.rows = [];
    this.renderChips();
    this.bind();
    this.recalc();
  };

  BACCalculatorEl.prototype.renderChips = function () {
    var wrap = this.querySelector('[data-bac-presets-wrap]');
    if (!wrap) return;
    var html = '';
    this.presets.forEach(function (p, i) {
      if (p.short) {
        html += '<button type="button" class="bac-chip" data-bac-chip="' + i + '" aria-label="Add ' + p.label + '">' +
          '<span class="bac-chip__name"><span class="bac-chip__plus" aria-hidden="true">+</span> ' + p.short + '</span>' +
          '<span class="bac-chip__meta">' + (p.detail || '') + '</span></button>';
      } else {
        html += '<button type="button" class="bac-chip" data-bac-chip="' + i + '">' +
          '<span class="bac-chip__plus" aria-hidden="true">+</span> ' + p.label + '</button>';
      }
    });
    // the custom-drink toggle lives in the grid as the last chip
    var panel = this.querySelector('[data-bac-custom]');
    html += '<button type="button" class="bac-chip bac-chip--custom" data-bac-custom-toggle aria-expanded="false"' +
      (panel && panel.id ? ' aria-controls="' + panel.id + '"' : '') + '>' +
      '<span class="bac-chip__name"><span class="bac-chip__plus" aria-hidden="true">+</span> Add a custom drink</span>' +
      '<span class="bac-chip__meta">your own volume &amp; ABV</span></button>';
    wrap.innerHTML = html;
  };

  BACCalculatorEl.prototype.bind = function () {
    var self = this;
    this.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-bac-chip]');
      if (chip) { self.addPreset(parseInt(chip.getAttribute('data-bac-chip'), 10)); return; }
      var custom = e.target.closest('[data-bac-custom-add]');
      if (custom) { self.addCustom(); return; }
      var toggle = e.target.closest('[data-bac-custom-toggle]');
      if (toggle) {
        var panel = self.querySelector('[data-bac-custom]');
        if (panel) {
          var open = panel.hasAttribute('hidden');
          if (open) panel.removeAttribute('hidden');
          else panel.setAttribute('hidden', '');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          toggle.classList.toggle('is-open', open);
          if (open) {
            var ml = panel.querySelector('[data-bac-custom-ml]');
            if (ml) ml.focus();
          }
        }
        return;
      }
      var step = e.target.closest('[data-bac-step]');
      if (step) {
        var idx = parseInt(step.getAttribute('data-bac-row'), 10);
        self.stepRow(idx, step.getAttribute('data-bac-step') === 'up' ? 1 : -1);
      }
    });
    this.addEventListener('input', function (e) {
      if (e.target.closest('[data-bac-custom-ml],[data-bac-custom-abv]')) return;
      self.recalc();
    });
    this.addEventListener('change', function () { self.recalc(); });
  };

  BACCalculatorEl.prototype.addPreset = function (i) {
    var p = this.presets[i];
    if (!p) return;
    var existing = this.rows.filter(function (r) { return r.key === p.key; })[0];
    if (existing) existing.count += 1;
    else this.rows.push({ key: p.key, label: p.label, ml: p.ml, abv: p.abv, count: 1 });
    this.renderRows();
    this.recalc();
  };

  BACCalculatorEl.prototype.addCustom = function () {
    var mlEl = this.querySelector('[data-bac-custom-ml]');
    var abvEl = this.querySelector('[data-bac-custom-abv]');
    var ml = parseFloat(mlEl && mlEl.value);
    var abv = parseFloat(abvEl && abvEl.value);
    if (!(ml > 0) || !(abv > 0) || abv > 100 || ml > 5000) return;
    this.rows.push({
      key: 'custom-' + this.rows.length + '-' + ml + '-' + abv,
      label: 'Custom drink (' + ml + ' mL, ' + abv + '%)',
      ml: ml, abv: abv, count: 1
    });
    if (mlEl) mlEl.value = '';
    if (abvEl) abvEl.value = '';
    this.renderRows();
    this.recalc();
  };

  BACCalculatorEl.prototype.stepRow = function (idx, delta) {
    var row = this.rows[idx];
    if (!row) return;
    row.count += delta;
    if (row.count <= 0) this.rows.splice(idx, 1);
    this.renderRows();
    this.recalc();
  };

  BACCalculatorEl.prototype.renderRows = function () {
    var wrap = this.querySelector('[data-bac-rows]');
    if (!wrap) return;
    if (!this.rows.length) {
      wrap.innerHTML = '<p class="bac-rows__empty">No drinks added yet. Tap a drink to add it here.</p>';
      return;
    }
    var html = '';
    this.rows.forEach(function (r, i) {
      var sd = BACCalc.standardDrinks(BACCalc.gramsOfAlcohol(r.ml, r.abv)) * r.count;
      html += '<div class="bac-row">' +
        '<span class="bac-row__label">' + r.label + '</span>' +
        '<span class="bac-row__controls">' +
        '<button type="button" class="bac-step" data-bac-step="down" data-bac-row="' + i + '" aria-label="Remove one ' + r.label + '">&minus;</button>' +
        '<span class="bac-row__count" aria-live="off">' + r.count + '</span>' +
        '<button type="button" class="bac-step" data-bac-step="up" data-bac-row="' + i + '" aria-label="Add one ' + r.label + '">+</button>' +
        '</span>' +
        '<span class="bac-row__sd">' + sd.toFixed(1) + ' std drinks</span>' +
        '</div>';
    });
    wrap.innerHTML = html;
  };

  BACCalculatorEl.prototype.readInputs = function () {
    var sexEl = this.querySelector('input[name="bac-sex"]:checked');
    var weightEl = this.querySelector('[data-bac-weight]');
    var licenceEl = this.querySelector('[data-bac-licence]');
    var hoursEl = this.querySelector('[data-bac-hours]');
    var weight = Math.min(250, Math.max(30, parseFloat(weightEl && weightEl.value) || 75));
    var hours = Math.min(48, Math.max(0, parseFloat(hoursEl && hoursEl.value) || 0));
    var sex = sexEl ? sexEl.value : 'male';
    var licence = licenceEl ? licenceEl.value : 'full';
    return {
      r: BACCalc.R[sex] || BACCalc.R.male,
      weightKg: weight,
      hours: hours,
      licence: licence,
      limit: BACCalc.LIMITS.hasOwnProperty(licence) ? BACCalc.LIMITS[licence] : 0.05
    };
  };

  BACCalculatorEl.prototype.totalGrams = function () {
    return this.rows.reduce(function (sum, r) {
      return sum + BACCalc.gramsOfAlcohol(r.ml, r.abv) * r.count;
    }, 0);
  };

  BACCalculatorEl.prototype.recalc = function () {
    var inp = this.readInputs();
    var grams = this.totalGrams();
    var o = { grams: grams, weightKg: inp.weightKg, r: inp.r };
    var bac = BACCalc.bacAt({ grams: grams, weightKg: inp.weightKg, r: inp.r, hours: inp.hours });
    var sdTotal = BACCalc.standardDrinks(grams);

    this.setText('[data-bac-bac]', bac.toFixed(3));
    this.setText('[data-bac-sd-total]', sdTotal.toFixed(1));

    var untilLegal = Math.max(0, BACCalc.hoursUntil(inp.limit, o) - inp.hours);
    var untilZero = Math.max(0, BACCalc.hoursUntil(0, o) - inp.hours);
    this.setText('[data-bac-until-legal]', inp.limit === 0 ? fmtHours(untilZero) : fmtHours(untilLegal));
    this.setText('[data-bac-until-zero]', fmtHours(untilZero));

    var status = this.querySelector('[data-bac-status]');
    if (status) {
      var cls, label;
      if (inp.limit === 0) {
        cls = bac > 0 ? 'is-over' : 'is-ok';
        label = bac > 0 ? 'Over your zero limit' : 'Zero limit: no alcohol detected on these inputs';
      } else if (bac >= inp.limit) {
        cls = 'is-over'; label = 'Over the ' + inp.limit.toFixed(2) + ' limit';
      } else if (inp.limit - bac <= 0.02 && bac > 0) {
        cls = 'is-warn'; label = 'Close to the ' + inp.limit.toFixed(2) + ' limit';
      } else {
        cls = 'is-ok'; label = 'Estimated under the ' + inp.limit.toFixed(2) + ' limit';
      }
      status.className = 'bac-badge ' + cls;
      status.textContent = label;
      var figure = this.querySelector('[data-bac-bac]');
      if (figure) figure.className = 'bac-result__figure ' + cls;
    }

    var effect = EFFECTS.filter(function (e) { return bac >= e.min; })[0];
    this.setText('[data-bac-effects]', effect ? effect.text : EFFECTS[EFFECTS.length - 1].text);

    this.drawChart(o, inp);
  };

  BACCalculatorEl.prototype.setText = function (sel, text) {
    var el = this.querySelector(sel);
    if (el) el.textContent = text;
  };

  BACCalculatorEl.prototype.drawChart = function (o, inp) {
    var svg = this.querySelector('[data-bac-chart]');
    if (!svg) return;
    var W = 600, H = 220, padL = 46, padR = 16, padT = 14, padB = 30;
    var pts = BACCalc.curve(o);
    var maxH = Math.max(4, pts[pts.length - 1].h);
    var maxBac = Math.max(0.08, BACCalc.peak(o) * 1.15);
    var x = function (h) { return padL + (h / maxH) * (W - padL - padR); };
    var y = function (b) { return H - padB - (b / maxBac) * (H - padT - padB); };
    var line = pts.map(function (p) { return x(p.h).toFixed(1) + ',' + y(p.bac).toFixed(1); }).join(' ');
    var nowX = x(Math.min(inp.hours, maxH));
    var limitY = y(0.05);
    var html =
      '<line x1="' + padL + '" y1="' + (H - padB) + '" x2="' + (W - padR) + '" y2="' + (H - padB) + '" class="bac-chart__axis"/>' +
      '<line x1="' + padL + '" y1="' + padT + '" x2="' + padL + '" y2="' + (H - padB) + '" class="bac-chart__axis"/>' +
      '<line x1="' + padL + '" y1="' + limitY + '" x2="' + (W - padR) + '" y2="' + limitY + '" class="bac-chart__limit"/>' +
      '<text x="' + (W - padR) + '" y="' + (limitY - 5) + '" text-anchor="end" class="bac-chart__label">0.05 limit</text>' +
      '<polyline points="' + line + '" class="bac-chart__line"/>' +
      '<line x1="' + nowX + '" y1="' + padT + '" x2="' + nowX + '" y2="' + (H - padB) + '" class="bac-chart__now"/>' +
      // when "now" sits on top of the origin, one merged label beats two overlapping ones
      (nowX - x(0) < 60
        ? '<text x="' + padL + '" y="' + (H - padB + 20) + '" text-anchor="start" class="bac-chart__label">first drink (now)</text>'
        : '<text x="' + nowX + '" y="' + (H - padB + 20) + '" text-anchor="middle" class="bac-chart__label">now</text>' +
          '<text x="' + padL + '" y="' + (H - padB + 20) + '" text-anchor="start" class="bac-chart__label">first drink</text>') +
      '<text x="' + (W - padR) + '" y="' + (H - padB + 20) + '" text-anchor="end" class="bac-chart__label">' + Math.ceil(maxH) + ' h</text>' +
      '<text x="12" y="' + padT + '" text-anchor="start" transform="rotate(90 12 ' + padT + ')" class="bac-chart__label">BAC %</text>';
    svg.innerHTML = html;
  };

  customElements.define('bac-calculator', BACCalculatorEl);
})(typeof window !== 'undefined' ? window : globalThis);
